import { useCallback, useMemo, useEffect } from 'react';
import { usePermissions } from './usePermissions';
import { useAuthentication } from './useAuthentication';
import { useNavigate } from 'react-router-dom';

/**
 * Hook pour déterminer la page de destination après connexion
 * basée sur les rôles et permissions de l'utilisateur
 */
export const useDefaultRoute = () => {
  const { permissions } = usePermissions();
  const { user } = useAuthentication();
  const navigate = useNavigate();
  
  const routePriority = [
    { path: '/dashboard', permission: 'get dashboard' },
    { path: '/tax/dashboard', permission: 'get taxdashboard' },
    { path: '/users/profile', permission: 'get profile' },
    { path: '/clients', permission: 'get company' },
    { path: '/products', permission: 'get product' },
    { path: '/dealers', permission: 'get dealer' },
    { path: '/users', permission: 'get systemuser' },
    { path: '/documents', permission: 'get member' },
    { path: '/qrcode-requests', permission: 'get qrcodes' },
    { path: '/complaints', permission: 'get complaint' },
    { path: '/logs', permission: 'get log' },
  ];

  // Calculer la route par défaut
  const defaultRoute = useMemo(() => {
    if (permissions.length === 0) {
      return '/dashboard';
    }
    
    // Déterminer le dashboard selon le rôle de l'utilisateur
    // Normaliser le rôle (supprimer les espaces et convertir en minuscules)
    const userRole = user?.user?.roles?.toLowerCase().replace(/\s/g, '') || '';
    
    // Rôles qui doivent voir taxdashboard (normalisés sans espaces)
    const taxDashboardRoles = [
      "taxsuperadmin",
      "taxadmin", 
      "taxcollector",
      "taxsupervisor",
      "dealer",
      "taxpayer", // debitor
      "debitor"
    ];
    
    // Si l'utilisateur a un rôle tax, prioriser taxdashboard
    if (userRole && taxDashboardRoles.includes(userRole)) {
      // Vérifier si l'utilisateur a la permission pour taxdashboard
      const checkPermission = (permission: string) => {
        const requestedPermission = permission.toLowerCase().trim();
        
        return permissions.some((p: any) => {
          if (!p.model || !p.permission) return false;
          
          const fullPermission = `${p.permission} ${p.model}`.toLowerCase();
          const actionOnly = p.permission.toLowerCase();
          
          return fullPermission === requestedPermission || actionOnly === requestedPermission;
        });
      };
      
      if (checkPermission('get taxdashboard')) {
        return '/tax/dashboard';
      }
    }
    
    // Pour les autres rôles (admin, company, user), utiliser dashboard
    // Fonction locale pour vérifier les permissions
    const checkPermission = (permission: string) => {
      const requestedPermission = permission.toLowerCase().trim();
      
      return permissions.some((p: any) => {
        if (!p.model || !p.permission) return false;
        
        const fullPermission = `${p.permission} ${p.model}`.toLowerCase();
        const actionOnly = p.permission.toLowerCase();
        
        return fullPermission === requestedPermission || actionOnly === requestedPermission;
      });
    };
    
    for (const route of routePriority) {
      if (checkPermission(route.permission)) {
        return route.path;
      }
    }
    
    return '/dashboard';
  }, [permissions.length, permissions.map(p => `${p.model}-${p.permission}`).join('|'), user?.user?.roles]);

  // Effect pour rediriger quand les permissions sont chargées
  useEffect(() => {
    if (permissions.length > 0 && window.location.pathname !== defaultRoute) {
      navigate(defaultRoute, { replace: true });
    }
  }, [permissions.length, defaultRoute, navigate]);

  const getDefaultRoute = useCallback(() => {
    return defaultRoute;
  }, [defaultRoute]);

  return { getDefaultRoute };
};